import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { AlertCircle, Eye, EyeOff, Lock, CheckCircle2 } from 'lucide-react';
import PasswordStrengthGuide, { getPasswordValidation } from '../../Components/common/PasswordStrengthGuide';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const validation = getPasswordValidation(formData.password);
  const isMatch = formData.confirmPassword && formData.password === formData.confirmPassword;
  const isMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  useEffect(() => {
    const verifyToken = async () => {
      try {
        await api.get(`/auth/verify-reset-token/${token}`);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'This reset link is invalid or has already expired.');
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validation.isValid) {
      toast.error(validation.firstError || 'Please fulfill all password security requirements');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setResetting(true);

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        password: formData.password
      });

      if (response.data.success) {
        toast.success('Password reset successfully! Please login with your new password.');
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-[32px] shadow-2xl p-8 max-w-md w-full text-center space-y-6 border border-slate-100 animate-in fade-in zoom-in-95 duration-300">
          <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto text-rose-500 shadow-inner">
            <AlertCircle className="w-10 h-10 text-rose-500 animate-pulse" />
          </div>
          <h2 className="text-2xl font-black text-[#143E78] uppercase tracking-tight">Link Expired</h2>
          <p className="text-sm font-semibold text-rose-500 leading-relaxed">
            {error}
          </p>
          <div className="pt-4 border-t border-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest leading-relaxed">
            Please request a new password reset link from the login page or contact support.
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-[#143E78] text-white rounded-xl font-bold hover:bg-[#0f2d57] transition-all text-sm"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-100 shadow-sm">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">Reset Your Password</h1>
          <p className="text-sm text-gray-500 mt-1 font-medium">
            Create a strong, secure password for your DOXEZ CRM account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Input */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
              New Password <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 pr-12 border rounded-xl font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all ${
                  formData.password && validation.isValid
                    ? 'border-emerald-400 bg-emerald-50/20'
                    : 'border-gray-200 focus:border-blue-500'
                }`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Real-time Password Requirements Checklist & Strength Bar */}
            <PasswordStrengthGuide password={formData.password} />
          </div>

          {/* Confirm Password Input */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
              Confirm Password <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 pr-12 border rounded-xl font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all ${
                  isMismatch
                    ? 'border-rose-400 bg-rose-50/30'
                    : isMatch
                    ? 'border-emerald-400 bg-emerald-50/20'
                    : 'border-gray-200 focus:border-blue-500'
                }`}
                placeholder="Re-enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {isMismatch && (
              <p className="mt-1.5 text-xs text-rose-500 font-semibold flex items-center gap-1">
                <AlertCircle size={14} /> Passwords do not match
              </p>
            )}
            {isMatch && (
              <p className="mt-1.5 text-xs text-emerald-600 font-semibold flex items-center gap-1">
                <CheckCircle2 size={14} /> Passwords match perfectly
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={resetting || !validation.isValid || isMismatch}
            className="w-full bg-[#143E78] text-white py-3.5 px-4 rounded-xl font-bold hover:bg-[#0f2c56] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/10 text-sm tracking-wide"
          >
            {resetting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:text-blue-700 text-sm font-semibold hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
